// terrain.ts — Lague-style runtime terrain builder. Takes an icosphere, evaluates
// summed fractal noise layers per vertex (layer 0 doubling as a continent mask),
// displaces each vertex radially by radius*(1+max(0,elevation)*scale), records the
// elevation min/max, and stores the normalized elevation + latitude as per-vertex
// attributes so the SURFACE SHADER can classify biomes per fragment (crisp coasts
// and snow lines, not Gouraud-smeared vertex colours). Deterministic per
// (seed, kind, radius, detail); results are LRU-cached so re-entering a system
// doesn't regenerate.
import { BufferAttribute, IcosahedronGeometry, Vector3 } from 'three';
import type { BufferGeometry } from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { PlanetKind } from '../../types';
import { alea, createNoise3D } from './noise';
import { makeFilter } from './noiseFilters';
import type { NoiseFilter } from './noiseFilters';
import { PLANET_PRESETS } from './presets';
import type { ClimateRaster } from './climateRaster';

// Global realism knob on top of each kind's displacementScale. Real planetary
// relief is tiny relative to radius (Everest is ~0.0014 of Earth's radius), so the
// per-kind budgets are deliberately exaggerated for readability; this factor pulls
// them back toward a flatter, more believable profile without disturbing the
// relative roughness between kinds. Lower = flatter.
const RELIEF_BUDGET_SCALE = 0.6;

// The server continent map's land amount (0..1) is a gentler signal than the raw noise the
// client-only path displaces, so scale it up to a comparable peak height. Tuned by eye.
const SERVER_RELIEF = 3.0;

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

// Quintic smootherstep — zero 1st/2nd derivative at 0 and 1, so bilinear taps weighted by it
// join C1-smooth across the coarse raster's cell boundaries instead of the piecewise-linear
// kinks a raw bilinear sample traces (the blocky "torn-foil" coastline).
function smootherstep(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

// sampleElevSmooth samples an equirect uint8 plane by sphere direction like sampleEquirect, but
// with smootherstep-weighted bilinear interpolation so the coastline iso-contour (where the
// elevation crosses sea level) is a smooth curve rather than grid-aligned steps. Longitude
// wraps, latitude clamps. Returns 0..1.
function sampleElevSmooth(
  plane: Uint8Array,
  nx: number,
  ny: number,
  dx: number,
  dy: number,
  dz: number,
): number {
  const lat = Math.asin(Math.max(-1, Math.min(1, dy)));
  const lon = Math.atan2(dz, dx);
  const fx = ((lon + Math.PI) / (2 * Math.PI)) * nx - 0.5;
  let fy = ((Math.PI / 2 - lat) / Math.PI) * ny - 0.5;
  if (fy < 0) fy = 0;
  if (fy > ny - 1) fy = ny - 1;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = smootherstep(fx - x0);
  const ty = smootherstep(fy - y0);
  const wrap = (x: number) => ((x % nx) + nx) % nx;
  const ix0 = wrap(x0);
  const ix1 = wrap(x0 + 1);
  const iy0 = Math.max(0, Math.min(ny - 1, y0));
  const iy1 = Math.max(0, Math.min(ny - 1, y0 + 1));
  const v00 = plane[iy0 * nx + ix0];
  const v10 = plane[iy0 * nx + ix1];
  const v01 = plane[iy1 * nx + ix0];
  const v11 = plane[iy1 * nx + ix1];
  const top = v00 + (v10 - v00) * tx;
  const bot = v01 + (v11 - v01) * tx;
  return (top + (bot - top) * ty) / 255;
}

export interface PlanetGeometryResult {
  geometry: BufferGeometry;
  /** A smooth sphere at sea level carrying the SAME aElevation/aLatitude as the
   *  terrain, so the ocean shell can discard land fragments (ripples on water only,
   *  coastline matching the surface exactly). */
  oceanGeometry: BufferGeometry;
}

// FNV-1a over the planet id -> uint32 seed. Stable across reloads/clients.
export function hashSeed(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// makeOceanShell clones a finished terrain geometry and flattens it to a smooth sea-level
// sphere, keeping aElevation so the ocean shader discards land along the same coastline.
function makeOceanShell(geometry: BufferGeometry, baseRadius: number): BufferGeometry {
  const seaR = baseRadius * 1.001;
  const oceanGeometry = geometry.clone();
  const opos = oceanGeometry.getAttribute('position') as BufferAttribute;
  const d = new Vector3();
  for (let i = 0; i < opos.count; i++) {
    d.set(opos.getX(i), opos.getY(i), opos.getZ(i)).normalize();
    opos.setXYZ(i, d.x * seaR, d.y * seaR, d.z * seaR);
  }
  opos.needsUpdate = true;
  oceanGeometry.computeVertexNormals();
  oceanGeometry.computeBoundingSphere();
  return oceanGeometry;
}

function buildPlanetGeometry(
  seed: number,
  kind: PlanetKind,
  baseRadius: number,
  detail: number,
  climate?: ClimateRaster,
): PlanetGeometryResult {
  const preset = PLANET_PRESETS[kind];
  // IcosahedronGeometry is non-indexed (duplicated vertices), which makes
  // computeVertexNormals() produce flat per-face normals — a faceted, low-poly look. Merge
  // into an indexed geometry so normals smooth across faces and we evaluate noise once per
  // unique vertex.
  const geometry = mergeVertices(new IcosahedronGeometry(1, detail));
  const pos = geometry.getAttribute('position') as BufferAttribute;
  const count = pos.count;
  const elevAttr = new Float32Array(count); // normalized 0..1, for the shader
  const latAttr = new Float32Array(count); // unit-sphere y, for the snow line
  const dir = new Vector3();

  if (climate) {
    // Server-authoritative continents: the rendered coastline IS where the baked climate
    // says "ocean", so ice/vegetation sampled from the same maps align with what's drawn.
    // Sample the equirect elevation by direction; ocean depth maps [0, serverSea) -> [0,
    // seaLevel01) (deepest -> 0, well below the coastline so the shader warp can't flip it),
    // land maps [serverSea, 1] -> [seaLevel01, 1] with the client's POSITIVE ridged mountain
    // detail (never sinking land below the coast).
    const sea01 = preset.ramp.seaLevel01;
    const serverSea = Math.max(climate.seaLevel, 1e-4);
    const noise = createNoise3D(alea(seed));
    const mtn: NoiseFilter[] = preset.layers.slice(1).map((l) => makeFilter(noise, l));
    // A distinct low-frequency stream domain-warps the sample direction so the coastline picks
    // up organic sub-grid wiggle instead of tracing the coarse raster grid. Amplitude ~1 cell
    // (~3°): enough to naturalize the edge without moving continents; with the smootherstep
    // sampler the coasts read smooth and organic rather than blocky.
    const warp = createNoise3D(alea((seed ^ 0x9e3779b9) >>> 0));
    const WARP_AMP = 0.045;
    const WARP_FREQ = 2.6;
    for (let i = 0; i < count; i++) {
      dir.set(pos.getX(i), pos.getY(i), pos.getZ(i)).normalize();
      const wx = dir.x + WARP_AMP * warp(dir.x * WARP_FREQ, dir.y * WARP_FREQ, dir.z * WARP_FREQ);
      const wy =
        dir.y + WARP_AMP * warp(dir.x * WARP_FREQ + 31.4, dir.y * WARP_FREQ + 31.4, dir.z * WARP_FREQ + 31.4);
      const wz =
        dir.z + WARP_AMP * warp(dir.x * WARP_FREQ + 62.8, dir.y * WARP_FREQ + 62.8, dir.z * WARP_FREQ + 62.8);
      const wl = Math.sqrt(wx * wx + wy * wy + wz * wz) || 1;
      const sElev = sampleElevSmooth(climate.elevation, climate.nx, climate.ny, wx / wl, wy / wl, wz / wl);
      let aE: number;
      let disp = 0; // radial displacement fraction; ocean stays a smooth sphere (disp 0)
      if (sElev < serverSea) {
        aE = sea01 * (sElev / serverSea); // ocean: deepest -> 0, coast -> seaLevel01
      } else {
        const land01 = (sElev - serverSea) / Math.max(1 - serverSea, 1e-4);
        let m = 0;
        for (let l = 0; l < mtn.length; l++) {
          if (preset.layers[l + 1]?.enabled) {
            m += mtn[l].evaluate(dir.x, dir.y, dir.z);
          }
        }
        // Mountains ride on the land and taper into the coast (like the client mask path), so a
        // shoreline vertex doesn't needle up out of the flat sea.
        const mtn01 = Math.max(0, m) * 0.35 * clamp01(land01 * 3);
        const relief = clamp01(land01 * 0.6 + mtn01); // 0..1 land height for the biome paint
        aE = sea01 + (1 - sea01) * relief; // land -> [seaLevel01, 1]
        // Displacement boosts ONLY the low-frequency continent base; the mountain term keeps its
        // natural amplitude. SERVER_RELIEF previously scaled BOTH, which tripled the mountain
        // noise's per-vertex slopes into spiky needles + erratic normals on the fine focused mesh.
        disp = land01 * 0.6 * SERVER_RELIEF + mtn01;
      }
      const r = baseRadius * (1 + disp * preset.displacementScale * RELIEF_BUDGET_SCALE);
      pos.setXYZ(i, dir.x * r, dir.y * r, dir.z * r);
      elevAttr[i] = aE;
      latAttr[i] = dir.y;
    }
  } else {
    // Client-only procedural continents (baked climate not loaded yet / surfaceless body):
    // summed fractal noise, layer 0 doubling as the continent mask. Original path.
    const noise = createNoise3D(alea(seed));
    const filters: NoiseFilter[] = preset.layers.map((layer) => makeFilter(noise, layer));
    const elevations = new Float32Array(count);
    let minE = Infinity;
    let maxE = -Infinity;
    for (let i = 0; i < count; i++) {
      dir.set(pos.getX(i), pos.getY(i), pos.getZ(i)).normalize();
      let firstLayerValue = 0;
      let elevation = 0;
      if (filters.length > 0) {
        firstLayerValue = filters[0].evaluate(dir.x, dir.y, dir.z);
        if (preset.layers[0].enabled) {
          elevation = firstLayerValue;
        }
      }
      for (let l = 1; l < filters.length; l++) {
        if (!preset.layers[l].enabled) {
          continue;
        }
        const mask = preset.layers[l].useFirstLayerAsMask ? firstLayerValue : 1;
        elevation += filters[l].evaluate(dir.x, dir.y, dir.z) * mask;
      }
      elevations[i] = elevation; // unscaled, may be negative
      if (elevation < minE) minE = elevation;
      if (elevation > maxE) maxE = elevation;
    }
    const span = maxE - minE || 1;
    for (let i = 0; i < count; i++) {
      dir.set(pos.getX(i), pos.getY(i), pos.getZ(i)).normalize();
      const e = elevations[i];
      const displaced = Math.max(0, e);
      const r = baseRadius * (1 + displaced * preset.displacementScale * RELIEF_BUDGET_SCALE);
      pos.setXYZ(i, dir.x * r, dir.y * r, dir.z * r);
      elevAttr[i] = (e - minE) / span;
      latAttr[i] = dir.y;
    }
  }

  pos.needsUpdate = true;
  geometry.setAttribute('aElevation', new BufferAttribute(elevAttr, 1));
  geometry.setAttribute('aLatitude', new BufferAttribute(latAttr, 1));
  geometry.computeVertexNormals(); // must run after positions are final
  geometry.computeBoundingSphere(); // displacement changed the bounds
  return { geometry, oceanGeometry: makeOceanShell(geometry, baseRadius) };
}

const cache = new Map<string, PlanetGeometryResult>();
const MAX_CACHED = 24;

// LRU cache keyed on the inputs: planets unmount when the player zooms back out to the
// galaxy, so re-entering a system would otherwise regenerate every body.
export function getPlanetGeometry(
  seed: number,
  kind: PlanetKind,
  baseRadius: number,
  detail: number,
  climate?: ClimateRaster,
): PlanetGeometryResult {
  // The baked elevation is deterministic per planet (already in `seed`), so a present/absent
  // marker is enough to cache the server-continent and noise meshes separately.
  const key = `${seed}|${kind}|${baseRadius}|${detail}|${climate ? 'srv' : 'noise'}`;
  const hit = cache.get(key);
  if (hit) {
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  const result = buildPlanetGeometry(seed, kind, baseRadius, detail, climate);
  cache.set(key, result);
  if (cache.size > MAX_CACHED) {
    const oldest = cache.keys().next().value as string;
    const evicted = cache.get(oldest);
    evicted?.geometry.dispose();
    evicted?.oceanGeometry.dispose();
    cache.delete(oldest);
  }
  return result;
}
